const template = document.createElement("template");

template.innerHTML = `
<style>
  :host{
    --ctl-font: var(--ctl-font-sans, system-ui,-apple-system,Segoe UI,Roboto,Arial);
    --ctl-focus-ring: var(--ctl-focus-ring, 0 0 0 3px rgba(79,70,229,.15));

    display:block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .wrap{
    display:flex;
    gap: var(--ctl-space-3-5);
    align-items:flex-start;
    justify-content: space-between;
    min-width: 0;
  }

  .left{
    min-width: 0;
    display:flex;
    flex-direction: column;
    gap: var(--ctl-space-1-5);
  }

  .titleRow{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2-5);
    min-width: 0;
  }

  .title{
    font-weight: var(--ctl-font-weight-extrabold);
    letter-spacing: var(--ctl-tracking-tight);
    line-height: var(--ctl-leading-tight);
    margin: 0;
    font-size: var(--ctl-size-icon-lg);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :host([size="sm"]) .title{ font-size: var(--ctl-space-5); }
  :host([size="lg"]) .title{ font-size: calc(var(--ctl-space-6) - var(--ctl-space-0-5)); }

  .subtitle{
    color: var(--ctl-color-muted);
    font-size: var(--ctl-font-size-md);
    line-height: var(--ctl-leading-normal);
    margin: 0;
  }

  .right{
    display:flex;
    gap: var(--ctl-space-2-5);
    align-items:center;
    justify-content:flex-end;
    flex-wrap: wrap;
    max-width: 52%;
  }

  :host([wrap-actions]) .right{
    max-width: none;
  }

  .meta{
    display:flex;
    align-items:center;
    gap: var(--ctl-space-2-5);
    flex-wrap: wrap;
  }

  .meta:empty{ display:none; }
  .subtitle:empty{ display:none; }

  .actions:empty{ display:none; }

  .divider{
    height: var(--ctl-border-width-thin);
    background: var(--ctl-color-border);
    margin-top: var(--ctl-space-3-5);
  }
  :host([no-divider]) .divider{ display:none; }

  /* Ensure slotted blocks don't break layout */
  ::slotted(*){
    max-width: 100%;
  }
</style>

<header class="wrap" part="header">
  <div class="left" part="left">
    <div part="breadcrumbs"><slot name="breadcrumbs"></slot></div>

    <div class="titleRow" part="titleRow">
      <h1 class="title" part="title"></h1>
      <div class="meta" part="meta"><slot name="meta"></slot></div>
    </div>

    <p class="subtitle" part="subtitle"></p>
    <slot></slot>
  </div>

  <div class="right" part="right">
    <div class="actions" part="actions"><slot name="actions"></slot></div>
  </div>
</header>

<div class="divider" part="divider"></div>
`;

export class CtlPageHeader extends HTMLElement {
  static get observedAttributes() {
    return ["title", "subtitle", "size", "no-divider", "wrap-actions"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._titleEl = this.shadowRoot.querySelector(".title");
    this._subtitleEl = this.shadowRoot.querySelector(".subtitle");
  }

  connectedCallback() {
    if (!this.hasAttribute("size")) this.setAttribute("size", "md");
    this._sync();
  }

  attributeChangedCallback() {
    this._sync();
  }

  // attrs
  get title() { return this.getAttribute("title") || ""; }
  set title(v) { this.setAttribute("title", String(v ?? "")); }

  get subtitle() { return this.getAttribute("subtitle") || ""; }
  set subtitle(v) { this.setAttribute("subtitle", String(v ?? "")); }

  get size() { return this.getAttribute("size") || "md"; }
  set size(v) {
    const next = (v === "sm" || v === "lg") ? v : "md";
    this.setAttribute("size", next);
  }

  get noDivider() { return this.hasAttribute("no-divider"); }
  set noDivider(v) { v ? this.setAttribute("no-divider","") : this.removeAttribute("no-divider"); }

  get wrapActions() { return this.hasAttribute("wrap-actions"); }
  set wrapActions(v) { v ? this.setAttribute("wrap-actions","") : this.removeAttribute("wrap-actions"); }

  _sync() {
    this._titleEl.textContent = this.title || "";
    this._subtitleEl.textContent = this.subtitle || "";
  }
}

customElements.define("ctl-page-header", CtlPageHeader);
